import { AppVersionContext } from "AppVersionContext";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import { useHasDirtyForms } from "shared/hooks/useDirtyFormRegistry";

const VERSION_CHECK_INTERVAL = 1000 * 60 * 20; // 20 minutes

export const AppVersionProvider = ({ children }: PropsWithChildren) => {
  const currentVersionRef = useRef<string | null>(null);
  const currentBuildHashRef = useRef<string | null>(null);

  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [searchParams] = useSearchParams();
  const [hasNewVersion, setHasNewVersion] = useState(() =>
    searchParams.has("demoAppVersion"),
  );

  // Never interrupt someone mid-edit: hold the prompt until their forms are
  // saved or discarded, then show it.
  const hasDirtyForms = useHasDirtyForms();
  const showDialog = hasNewVersion && !hasDirtyForms;

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch("/version.json", { cache: "no-store" });

        if (!res.ok) {
          console.warn(
            `[AppVersion] Failed to fetch version.json: ${res.status} ${res.statusText}`,
          );
          return;
        }

        const data = await res.json();

        if (!data.version || !data.buildHash) {
          console.warn("[AppVersion] Invalid version.json format:", data);
          return;
        }

        if (!currentVersionRef.current) {
          // First load - initialize the refs
          currentVersionRef.current = data.version;
          currentBuildHashRef.current = data.buildHash;
          setCurrentVersion(data.version);
        } else if (currentBuildHashRef.current !== data.buildHash) {
          // New version detected
          setHasNewVersion(true);
        }
      } catch (error) {
        console.warn("[AppVersion] Error checking version:", error);
        // Will recheck on the next interval
      }
    };

    // Returning to a tab left open across a deploy is the common stale case.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    checkVersion();
    const id = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const handleDialogResponse = useCallback(async (response: boolean) => {
    if (response) {
      // User confirmed - reload the page
      window.location.reload();
    } else {
      // User dismissed - update refs to current version so we don't keep showing
      // the dialog for the same version, but we'll still detect new versions
      try {
        const res = await fetch("/version.json", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (data.buildHash) {
            currentBuildHashRef.current = data.buildHash;
          }
        }
      } catch {
        // Ignore errors when updating refs after dismissal
      }
      setHasNewVersion(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      showDialog,
      handleDialogResponse,
      currentVersion,
    }),
    [showDialog, handleDialogResponse, currentVersion],
  );

  return (
    <AppVersionContext.Provider value={value}>
      {children}
    </AppVersionContext.Provider>
  );
};
